import React, { useEffect, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import AutoNumeric from "autonumeric"
import { TextField, InputAdornment } from "@mui/material"
import { makeStyles } from "@mui/styles"

const useStyles = makeStyles({
  textField: props => ({
    textAlign: props.textAlign || "right",
  }),
})

function CurrencyTextField({
  currencySymbol = "$",
  outputFormat = "number",
  preDefined = {},
  inputProps = {},
  InputProps = {},
  textAlign = "left",
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyPress,
  onKeyUp,
  onKeyDown,
  ...others
}) {
  const classes = useStyles({ textAlign })
  const inputRef = useRef(null)
  const autoNumericRef = useRef(null)

  // Inicializa AutoNumeric
  useEffect(() => {
    autoNumericRef.current = new AutoNumeric(inputRef.current, value, {
      minimumValue: 0.00,
      ...preDefined,
      ...others,
      onChange: undefined,
      onFocus: undefined,
      onBlur: undefined,
      onKeyPress: undefined,
      onKeyUp: undefined,
      onKeyDown: undefined,
      watchExternalChanges: false,
    })

    return () => {
      autoNumericRef.current?.remove()
    }
  }, [])

  // Atualiza quando value externo mudar
  useEffect(() => {
    if (
      autoNumericRef.current &&
      value !== undefined &&
      value !== null &&
      autoNumericRef.current.getNumber() !== value
    ) {
      autoNumericRef.current.set(value)
    }
  }, [value])

  const getValue = useCallback(() => {
    if (!autoNumericRef.current) return
    const valueMapper = {
      string: numeric => numeric.getNumericString(),
      number: numeric => numeric.getNumber(),
    }
    return valueMapper[outputFormat](autoNumericRef.current)
  }, [outputFormat])

  const callEventHandler = useCallback(
    (event, handler) => {
      if (!handler) return
      handler(event, getValue())
    },
    [getValue]
  )

  return (
    <TextField
      inputRef={inputRef}
      onChange={e => callEventHandler(e, onChange)}
      onFocus={e => callEventHandler(e, onFocus)}
      onBlur={e => callEventHandler(e, onBlur)}
      onKeyPress={e => callEventHandler(e, onKeyPress)}
      onKeyUp={e => callEventHandler(e, onKeyUp)}
      onKeyDown={e => callEventHandler(e, onKeyDown)}
      InputProps={{
        ...(currencySymbol
          ? { startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }
          : {}),
        ...InputProps,
      }}
      inputProps={{
        className: classes.textField,
        ...inputProps,
      }}
      {...others}
    />
  )
}

CurrencyTextField.propTypes = {
  type: PropTypes.oneOf(["text", "tel", "hidden"]),
  variant: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  textAlign: PropTypes.oneOf(["right", "left", "center"]),
  tabIndex: PropTypes.number,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  currencySymbol: PropTypes.string,
  decimalCharacter: PropTypes.string,
  decimalCharacterAlternative: PropTypes.string,
  decimalPlaces: PropTypes.number,
  decimalPlacesShownOnBlur: PropTypes.number,
  decimalPlacesShownOnFocus: PropTypes.number,
  digitGroupSeparator: PropTypes.string,
  leadingZero: PropTypes.oneOf(["allow", "deny", "keep"]),
  maximumValue: PropTypes.string,
  minimumValue: PropTypes.string,
  negativePositiveSignPlacement: PropTypes.oneOf(["l", "r", "p", "s"]),
  negativeSignCharacter: PropTypes.string,
  outputFormat: PropTypes.oneOf(["string", "number"]),
  selectOnFocus: PropTypes.bool,
  positiveSignCharacter: PropTypes.string,
  readOnly: PropTypes.bool,
  preDefined: PropTypes.object,
}

export const predefinedOptions = AutoNumeric.getPredefinedOptions()

export default CurrencyTextField
